import React, { useRef, useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

interface HealthAsthmaChartProps {
  location: string;
}

export const HealthAsthmaChart: React.FC<HealthAsthmaChartProps> = ({ location }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locationValue, setLocationValue] = useState<number | null>(null);
  const [californiaValue, setCaliforniaValue] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!location) return;

      try {
        setLoading(true);
        setError(null);

        // Get geofips for the location
        const geofips = await supabaseLocationService.getGeofips(location);
        console.log(`🔍 HealthAsthmaChart: Location="${location}", geofips=${geofips}`);
        if (!geofips) {
          throw new Error(`Could not find geofips for location: ${location}`);
        }

        // Determine geography type
        const { data: locationData } = await supabaseClient
          .from('population')
          .select('geography_type')
          .eq('geofips', String(geofips))
          .limit(1)
          .maybeSingle();

        let geographyType = locationData?.geography_type || 'City';
        console.log(`🔍 HealthAsthmaChart: geography_type=${geographyType}`);

        // Fetch location data - try multiple geography types if needed
        let locationCES4 = null;
        let locationError = null;
        
        // Try the determined geography type first
        const { data: ces4Data, error: ces4Error } = await supabaseClient
          .from('calenviroscreen_4_0_aggregated')
          .select('asthma')
          .eq('geofips', String(geofips))
          .eq('geography_type', geographyType)
          .limit(1)
          .maybeSingle();
        
        locationCES4 = ces4Data;
        locationError = ces4Error;
        
        // If not found, try other geography types
        if (!locationCES4 && !locationError) {
          for (const geoType of ['City', 'Place', 'County']) {
            if (geoType !== geographyType) {
              const { data: altData, error: altError } = await supabaseClient
                .from('calenviroscreen_4_0_aggregated')
                .select('asthma')
                .eq('geofips', String(geofips))
                .eq('geography_type', geoType)
                .limit(1)
                .maybeSingle();
              
              if (altData && !altError) {
                locationCES4 = altData;
                geographyType = geoType;
                console.log(`🔍 HealthAsthmaChart: Found data with geography_type=${geoType}`);
                break;
              }
            }
          }
        }

        console.log(`🔍 HealthAsthmaChart: Location data:`, locationCES4, `Error:`, locationError);

        if (locationError) {
          console.error('Error fetching location asthma data:', locationError);
          throw locationError;
        }

        // Fetch California state data
        const { data: californiaCES4, error: californiaError } = await supabaseClient
          .from('calenviroscreen_4_0_aggregated')
          .select('asthma')
          .eq('geofips', '6')
          .eq('geography_type', 'State')
          .limit(1)
          .maybeSingle();

        if (californiaError) {
          console.error('Error fetching California asthma data:', californiaError);
          throw californiaError;
        }

        const locValue = locationCES4?.asthma ?? null;
        const caValue = californiaCES4?.asthma ?? null;

        console.log(`🔍 HealthAsthmaChart: Location value=${locValue}, California value=${caValue}`);

        setLocationValue(locValue);
        setCaliforniaValue(caValue);

        // Create chart - show location data even if California data is missing
        if (chartRef.current && locValue !== null) {
          const ctx = chartRef.current.getContext('2d');
          if (ctx) {
            // Destroy existing chart if it exists
            if (chartInstance.current) {
              chartInstance.current.destroy();
            }

            // Prepare data - include California only if available
            const labels = caValue !== null ? [location, 'California Average'] : [location];
            const data = caValue !== null ? [locValue, caValue] : [locValue];
            const backgroundColor = caValue !== null 
              ? ['#3b82f6', '#10b981']  // Blue for location, Green for California
              : ['#3b82f6'];  // Just blue for location
            const borderColor = caValue !== null
              ? ['#2563eb', '#059669']
              : ['#2563eb'];

            chartInstance.current = new Chart(ctx, {
              type: 'bar',
              data: {
                labels: labels,
                datasets: [
                  {
                    label: 'Asthma Rate',
                    data: data,
                    backgroundColor: backgroundColor,
                    borderColor: borderColor,
                    borderWidth: 2
                  }
                ]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  },
                  title: {
                    display: true,
                    text: caValue !== null 
                      ? `Asthma Emergency Department Visits: ${location} vs California Average`
                      : `Asthma Emergency Department Visits: ${location}`,
                    font: {
                      size: 16,
                      weight: 'bold'
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y;
                        const label = context.label;
                        return `${label}: ${value !== null ? value.toFixed(2) : 'N/A'} (age-adjusted rate)`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Age-Adjusted Rate of Emergency Department Visits for Asthma'
                    }
                  }
                }
              }
            });
          }
        }
      } catch (err) {
        console.error('Error in HealthAsthmaChart:', err);
        setError(err instanceof Error ? err.message : 'Failed to load asthma data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>Loading asthma data...</div>;
  }

  if (error) {
    return <div style={{ textAlign: 'center', padding: '20px', color: '#ef4444' }}>Error: {error}</div>;
  }

  if (locationValue === null) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>No asthma data available for this location.</div>;
  }

  return (
    <div style={{ position: 'relative', height: '400px' }}>
      <canvas ref={chartRef} />
    </div>
  );
};

